import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import * as api from "./blogApi";

export const fetchPosts = createAsyncThunk("blog/fetchPosts", async (params, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.getPosts(params, token);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to fetch posts");
  }
});

export const fetchPostById = createAsyncThunk("blog/fetchPostById", async (id, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.getPostById(id, token);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to fetch post");
  }
});

export const createPost = createAsyncThunk("blog/createPost", async (data, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.createPost(data, token);
    toast.success("Post created successfully");
    return res.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to create post";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const updatePost = createAsyncThunk("blog/updatePost", async ({ id, data }, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.updatePost(id, data, token);
    toast.success("Post updated successfully");
    return res.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to update post";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const updatePostStatus = createAsyncThunk("blog/updatePostStatus", async ({ id, status }, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.updatePostStatus(id, status, token);
    toast.success(`Post ${status} successfully`);
    return { id, status, post: res.data };
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to update status";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const deletePost = createAsyncThunk("blog/deletePost", async (id, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    await api.deletePost(id, token);
    toast.success("Post deleted successfully");
    return id;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to delete post";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const saveRichText = createAsyncThunk("blog/saveRichText", async ({ id, contentJson }, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.saveRichText(id, { contentJson }, token);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to save content");
  }
});

export const reorderBlocks = createAsyncThunk("blog/reorderBlocks", async ({ id, order }, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.reorderBlocks(id, order, token);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to reorder blocks");
  }
});

const blogSlice = createSlice({
  name: "blog",
  initialState: {
    posts: [],
    currentPost: null,
    total: 0,
    page: 1,
    pages: 0,
    loading: false,        // list fetch
    loadingPost: false,    // fetchPostById
    loadingSave: false,    // createPost / updatePost
    loadingPublish: false, // updatePostStatus
    loadingRichText: false,// saveRichText
    error: null,
    filters: {
      search: "",
      status: "all",
      category: "",
      tag: "",
      featured: false,
    },
  },
  reducers: {
    setPage: (state, action) => { state.page = action.payload; },
    setFilters: (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    clearCurrentPost: (state) => { state.currentPost = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPosts.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.posts = payload.posts || payload || [];
        state.total = payload.total || 0;
        state.page = payload.page || 1;
        state.pages = payload.pages || 0;
      })
      .addCase(fetchPosts.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchPostById.pending, (state) => { state.loadingPost = true; state.error = null; })
      .addCase(fetchPostById.fulfilled, (state, action) => {
        state.loadingPost = false;
        state.currentPost = action.payload?.post || action.payload;
      })
      .addCase(fetchPostById.rejected, (state, action) => { state.loadingPost = false; state.error = action.payload; })

      .addCase(createPost.pending, (state) => { state.loadingSave = true; state.error = null; })
      .addCase(createPost.fulfilled, (state, action) => {
        state.loadingSave = false;
        const post = action.payload?.post || action.payload;
        if (post?._id) state.posts.unshift(post);
        state.currentPost = post;
      })
      .addCase(createPost.rejected, (state, action) => { state.loadingSave = false; state.error = action.payload; })

      .addCase(updatePost.pending, (state) => { state.loadingSave = true; state.error = null; })
      .addCase(updatePost.fulfilled, (state, action) => {
        state.loadingSave = false;
        const post = action.payload?.post || action.payload;
        if (post?._id) {
          const idx = state.posts.findIndex((p) => p._id === post._id);
          if (idx !== -1) state.posts[idx] = post;
          state.currentPost = post;
        }
      })
      .addCase(updatePost.rejected, (state, action) => { state.loadingSave = false; state.error = action.payload; })

      .addCase(updatePostStatus.pending, (state) => { state.loadingPublish = true; state.error = null; })
      .addCase(updatePostStatus.fulfilled, (state, action) => {
        state.loadingPublish = false;
        const { id, status } = action.payload;
        const idx = state.posts.findIndex((p) => p._id === id);
        if (idx !== -1) state.posts[idx].status = status;
        if (state.currentPost?._id === id) state.currentPost.status = status;
      })
      .addCase(updatePostStatus.rejected, (state, action) => { state.loadingPublish = false; state.error = action.payload; })

      .addCase(deletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((p) => p._id !== action.payload);
        if (state.currentPost?._id === action.payload) state.currentPost = null;
      })
      .addCase(deletePost.rejected, (state, action) => { state.error = action.payload; })

      .addCase(saveRichText.pending, (state) => { state.loadingRichText = true; })
      .addCase(saveRichText.fulfilled, (state, action) => {
        state.loadingRichText = false;
        const post = action.payload?.post || action.payload;
        if (state.currentPost && post) state.currentPost = { ...state.currentPost, ...post };
      })
      .addCase(saveRichText.rejected, (state) => { state.loadingRichText = false; });
  },
});

export const { setPage, setFilters, clearCurrentPost } = blogSlice.actions;
export default blogSlice.reducer;
